import { makeStyles } from '@material-ui/core/styles';
export const useTimelineViewStyles = makeStyles((theme) => ({
  paper: {
    boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
    border: 'none',
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
  },
  card: {
    boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
    border: 'none',
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
  },
  cardContent: {
    padding: 0,
  },
  header: {
    padding: theme.spacing(2),
    backgroundColor: 'white',
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #e0e0e0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  headerIcon: {
    fontSize: 24,
    color: theme.palette.primary.main,
    transition: theme.transitions.create(['transform'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      transform: 'scale(1.1)',
    },
  },
  headerTitle: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: '1.125rem',
    letterSpacing: '0.00735em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
  },
  expandButton: {
    transition: theme.transitions.create(['transform', 'background-color'], {
      duration: theme.transitions.duration.standard,
    }),
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'scale(1.05)',
    },
  },
  expandButtonRotated: {
    transform: 'rotate(180deg)',
    '&:hover': {
      transform: 'rotate(180deg) scale(1.05)',
    },
  },
  content: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    maxHeight: '70vh',
    overflowY: 'auto',
    overflowX: 'visible', // Allow markers to extend beyond container
    animation: '$fadeIn 400ms ease-out',
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.grey[200],
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[400],
      borderRadius: 4,
      '&:hover': {
        backgroundColor: theme.palette.grey[500],
      },
    },
  },
  timeMarkersContainer: {
    position: 'relative',
    height: 150,
    marginBottom: theme.spacing(3),
    backgroundColor: 'transparent',
    animation: '$slideInFromTop 500ms ease-out',
    overflowX: 'visible', // Allow time markers to extend beyond container
    overflowY: 'visible', // Allow time markers to extend beyond container
  },
  timeMarkersContent: {
    display: 'flex',
    alignItems: 'flex-start',
    height: '100%',
    position: 'relative',
    minWidth: 'fit-content'
  },
  // Timeline base line that runs across the full width
  timelineBaseLine: {
    position: 'absolute',
    top: '75px', // Middle of 150px container
    left: 0,
    right: 0,
    height: '3px',
    backgroundColor: '#bdbdbd',
    borderRadius: '2px',
    opacity: 0.8,
    zIndex: 1,
  },
  // Vertical line connecting time markers to baseline
  timeMarkerVerticalLine: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '3px',
    backgroundColor: 'transparent',
    border: 'none',
    borderLeft: '3px dotted #bdbdbd',
    opacity: 0.8,
    zIndex: 2,
  },
  // Container for individual time marker
  timeMarkerContainer: {
    position: 'absolute',
    transform: 'translateX(-50%)',
    top: 0,
    height: '100%',
  },
  // Time marker paper positioned above baseline
  timeMarkerPaperAbove: {
    opacity: '0.8',
    position: 'absolute',
    top: '20px', // 75px baseline - 55px offset
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: theme.palette.background.default,
    borderRadius: '4px',
    height: '36px',
    minWidth: '80px',
    maxWidth: '120px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 3,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
    padding: '6px 8px',
    '&:hover': {
      transform: 'translateX(-50%) translateY(-2px)',
      boxShadow: '0 6px 16px rgba(0,0,0,0.2), 0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  // Time marker paper positioned below baseline
  timeMarkerPaperBelow: {
    opacity: '0.8',
    position: 'absolute',
    top: '90px', // 75px baseline + 15px offset
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: theme.palette.background.default,
    borderRadius: '4px',
    height: '36px',
    minWidth: '80px',
    maxWidth: '120px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 3,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
    padding: '6px 8px',
    '&:hover': {
      transform: 'translateX(-50%) translateY(-2px)',
      boxShadow: '0 6px 16px rgba(0,0,0,0.2), 0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  // Vertical line from above marker to baseline
  timeMarkerLineAbove: {
    top: '56px', // From bottom of above marker (20px + 36px)
    height: '19px', // To baseline (75px - 56px)
  },
  // Vertical line from baseline to below marker
  timeMarkerLineBelow: {
    top: '78px', // From baseline (75px + 3px)
    height: '12px', // To top of below marker (90px - 78px)
  },
  // Individual time marker container
  timeMarker: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 200,
    flex: 1,
    zIndex: 2,
  },
  // Paper-based time marker similar to pipeline steps
  timeMarkerPaper: {
    opacity: 0.8,
    backgroundColor: '#ffffff',
    borderRadius: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: 'none',
    height: '32px',
    minWidth: '120px',
    transition: 'all 0.2s ease',
    position: 'relative',
    '&:hover': {
      transform: 'translateY(-2px)',
      zIndex: 20,
    },
  },
  // Start time marker paper (green shadow)
  timeMarkerPaperStart: {
    boxShadow: '0 2px 8px rgba(76, 175, 80, 0.2)',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
      transform: 'translateY(-2px)',
    },
  },
  // End time marker paper (red shadow)
  timeMarkerPaperEnd: {
    boxShadow: '0 2px 8px rgba(244, 67, 54, 0.2)',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
      transform: 'translateY(-2px)',
    },
  },
  // Regular time marker paper (blue left shadow)
  timeMarkerPaperRegular: {
    boxShadow: '4px 0 0 0 #2196f3, 0 2px 8px rgba(0,0,0,0.1)',
    '&:hover': {
      boxShadow: '6px 0 0 0 #2196f3, 0 4px 12px rgba(0,0,0,0.15)',
    },
  },
  // Time marker content
  timeMarkerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  // Time marker date text
  timeMarkerDate: {
    fontSize: '0.9rem !important',
    fontWeight: 600,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
    textAlign: 'center',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.6rem',
    },
  },
  // Time marker time text
  timeMarkerTime: {
    fontSize: '0.7rem !important',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    lineHeight: 1.1,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.55rem',
    },
  },
  // Time marker text below chip
  timeMarkerText: {
    fontSize: '0.65rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    textAlign: 'center',
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.6rem',
    },
  },
  // Enhanced time marker for active/current state
  timeMarkerActive: {
    '& $timeMarkerChip': {
      transform: 'scale(1.1)',
      boxShadow: `0 0 0 4px ${theme.palette.primary.main}20, 0 4px 12px rgba(0,0,0,0.25)`,
    },
    '& $timeMarkerText': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  milestonesContainer: {
    position: 'relative',
    height: 24,
    marginBottom: theme.spacing(3),
    animation: '$slideInFromTop 600ms ease-out',
    overflowX: 'visible', // Allow milestone markers to extend beyond container
    overflowY: 'visible', // Allow milestone lines to extend beyond container
  },
  gridLine: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: theme.palette.divider,
    transform: 'translateX(-50%)',
    zIndex: 1,
    opacity: 0.6,
  },
  milestone: {
    position: 'absolute',
    top: 0,
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: 70,
    zIndex: 2,
  },
  milestoneLabel: {
    fontSize: '0.875rem',
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(0.5),
    whiteSpace: 'nowrap',
    color: theme.palette.text.primary,
    textShadow: '0 1px 2px rgba(255,255,255,0.8)',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  },
  milestoneLine: {
    width: 4,
    height: 24,
    borderRadius: theme.spacing(0.5),
    boxShadow: theme.shadows[1],
  },
  milestoneChip: {
    fontSize: '0.9rem',
    fontWeight: 600,
    height: 24,
    borderRadius: theme.spacing(1.5),
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      fontSize: '0.9rem',
      fontWeight: 600,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.7rem',
      height: 22,
      '& .MuiChip-label': {
        fontSize: '0.7rem',
      },
    },
  },
  pipelineContainer: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 1.5,
    padding: theme.spacing(2.5),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[1],
    transition: theme.transitions.create(['box-shadow', 'transform'], {
      duration: theme.transitions.duration.standard,
    }),
    animation: '$slideInFromLeft 400ms ease-out',
    '&:hover': {
      boxShadow: theme.shadows[3],
      transform: 'translateY(-1px)',
    },
  },
  pipelineHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  pipelineName: {
    fontSize: '1rem',
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
    minWidth: 140,
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.9rem',
    },
  },
  pipelineDate: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightRegular,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  },
  stepsContainer: {
    position: 'relative',
  },
  milestoneVerticalLine: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 2,
    backgroundSize: '2px 6px',
    transform: 'translateX(-50%)',
    zIndex: 1,
    opacity: 0.4,
  },
  timeGridLine: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: '#f8f8f8',
    transform: 'translateX(-50%)',
    zIndex: 1,
  },
  stepLane: {
    position: 'relative',
    height: 28,
    display: 'flex',
    alignItems: 'center',
  },
  laneBackground: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '50%',
    height: 1,
    backgroundColor: '#f0f0f0',
    transform: 'translateY(-50%)',
    zIndex: 1,
  },
  stepBar: {
    position: 'absolute',
    top: '50%',
    height: 20,
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transform: 'translateY(-50%)',
    transition: 'all 0.2s ease',
    zIndex: 10,
    border: '1px solid rgba(255,255,255,0.3)',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    '&:hover': {
      transform: 'translateY(-60%)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 20,
    },
  },
  stepLabel: {
    color: 'white',
    fontSize: '0.8rem',
    fontWeight: 600,
    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
    },
  },
  statusIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: 'white',
    marginLeft: theme.spacing(0.5),
    flexShrink: 0,
  },
  runningIndicator: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    height: 2,
    width: '100%',
    animation: '$pulse 2s infinite',
  },
  legend: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid #e0e0e0',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    backgroundColor: 'white',
    padding: theme.spacing(2),
  },
  legendLabel: {
    fontSize: '0.8em',
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  },
  legendChip: {
    color: 'white',
    fontSize: '0.8em',
    height: 24,
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75em',
      height: 22,
    },
  },
  statusLegend: {
    marginLeft: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
  statusItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  statusIcon: {
    fontSize: 12,
  },
  statusText: {
    fontSize: '0.8em',
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75em',
    },
  },
  tooltipContent: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    fontSize: '1.1rem'
  },
  tooltipTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: 'white',
    fontSize: '1.1rem'
  },
  tooltipMetric: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
    ontSize: '1.1rem'
  },
  tooltipMetricKey: {
    color: 'rgba(255,255,255,0.8)',
    marginRight: theme.spacing(2),
    fontSize: '1.1rem'
  },
  tooltipMetricValue: {
    color: 'white',
    fontWeight: 500,
    fontSize: '1.1rem'
  },
  tooltipStatus: {
    color: 'rgba(255,255,255,0.6)',
    marginTop: theme.spacing(1),
    display: 'block',
    fontSize: '1.1rem'
  },
  // Horizontal Pipeline Layout Styles
  horizontalPipelineContainer: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1),
    border: '1px solid #f0f0f0',
    overflowX: 'hidden', // Remove individual scrolling
    minWidth: '100%', // Ensure container takes full width
  },
  pipelineHeadersRow: {
    display: 'none', // Change from 'none' to 'flex' to show headers
    position: 'relative',
    height: 80,
    marginBottom: theme.spacing(2),
    borderBottom: '2px solid #e0e0e0',
    minWidth: 'fit-content', // Allow container to expand beyond viewport
  },
  horizontalPipelineHeader: {
    position: 'relative', // Change from absolute to relative for flex layout
    minWidth: 300, // Set minimum width for each pipeline column
    flex: 1, // Allow expansion to fill available space
    padding: theme.spacing(1),
    borderRight: '1px solid #f0f0f0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fafbfc',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  pipelineTimeRange: {
    marginTop: theme.spacing(0.5),
    textAlign: 'center',
  },
  pipelineStartTime: {
    display: 'block',
    fontSize: '0.7rem',
    color: '#4caf50',
    fontWeight: 500,
  },
  pipelineEndTime: {
    display: 'block',
    fontSize: '0.7rem',
    color: '#f44336',
    fontWeight: 500,
  },
  horizontalStepsContainer: {
    backgroundColor: theme.palette.background.default,
    position: 'relative',
    minHeight: 200, // Ensure enough height for stacked steps
    display: 'flex', // Use flex layout for pipeline columns
    minWidth: 'fit-content', // Allow container to expand beyond viewport
  },
  pipelineColumn: {
    position: 'relative',
    minWidth: 300, // Set minimum width for each pipeline column
    flex: 1, // Allow expansion to fill available space
    height: 200, // Set fixed height for consistency
    '&:last-child': {
      borderRight: 'none',
    },
  },
  horizontalStepLane: {
    position: 'relative',
    height: 50, // Increased height for better visibility
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    borderBottom: '1px solid #f5f5f5',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  stepLaneLabel: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 120,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(1),
    backgroundColor: '#f8f9fa',
    borderRight: '1px solid #e0e0e0',
    zIndex: 5,
  },
  stepLaneName: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  horizontalLaneBackground: {
    position: 'absolute',
    left: 120,
    right: 0,
    top: '50%',
    height: 2,
    backgroundColor: '#f0f0f0',
    transform: 'translateY(-50%)',
    zIndex: 1,
  },
  horizontalLaneBackgroundNoLabel: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '50%',
    height: 2,
    backgroundColor: '#f0f0f0',
    transform: 'translateY(-50%)',
    zIndex: 1,
  },
  horizontalStepBar: {
    position: 'absolute',
    top: '50%',
    height: 36, // Increased height
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transform: 'translateY(-50%)',
    transition: 'all 0.2s ease',
    zIndex: 10,
    border: '2px solid rgba(255,255,255,0.5)',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    marginLeft: 120, // Account for lane label width
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': {
      transform: 'translateY(-60%)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 20,
    },
  },
  horizontalStepBarNoLabel: {
    position: 'absolute',
    top: '50%',
    height: 36, // Increased height
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transform: 'translateY(-50%)',
    transition: 'all 0.2s ease',
    zIndex: 10,
    border: '2px solid rgba(255,255,255,0.5)',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    marginLeft: 0, // No offset for lane label
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': {
      transform: 'translateY(-60%)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 20,
    },
  },
  // Paper-based step bar styles with white background and color bar
  paperStepBar: {
    opacity: 0.8,
    position: 'absolute',
    backgroundColor: '#ffffff',
    borderRight: '8px solid #cccc',
    borderRadius: '2px', // Minimal border radius for crisp appearance
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
    cursor: 'pointer',
    border: 'none',
    minHeight: '40px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth transition for hover effects
    zIndex: 10,
    '&:hover': {
      transform: 'translateY(-2px)',
      zIndex: 20,
    },
  },
  // Enhanced styles for narrow step hover effect
  paperStepBarNarrow: {
    '&:hover': {
      opacity: 1,
      transform: 'translateY(-2px)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: '0 8px 25px rgba(0,0,0,0.15), 0 4px 10px rgba(0,0,0,0.1)',
      zIndex: 30,
    },
  },
  paperStepBarHorizontal: {
    top: `${60 * 0 + 10}px`, // Will be overridden by inline style for lane positioning
    transform: 'none',
  },
  paperStepBarRegular: {
    left: '0%', // Will be overridden by inline style for positioning
    width: '0%', // Will be overridden by inline style for width
    top: '50%',
    transform: 'translateY(-50%)',
  },
  // Enhanced step label styles for Paper components
  paperStepLabel: {
    color: theme.palette.text.primary,
    fontSize: '0.9rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
    },
  },
  // Duration text styles
  stepDuration: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    fontWeight: 500,
    marginRight: theme.spacing(1),
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.65rem',
    },
  },
  // Enhanced status indicator for Paper components
  paperIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 18,
    borderRadius: '50%',
    marginRight: theme.spacing(0.5),
    color: theme.palette.text.primary,
    flexShrink: 0
  },
  // Enhanced status icon styles
  paperStatusIcon: {
    fontSize: 14
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.5 },
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  },
  '@keyframes slideInFromTop': {
    '0%': {
      opacity: 0,
      transform: 'translateY(-20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  '@keyframes slideInFromLeft': {
    '0%': {
      opacity: 0,
      transform: 'translateX(-30px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
  '@keyframes slideInFromRight': {
    '0%': {
      opacity: 0,
      transform: 'translateX(30px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
}));
